import { TOAST_STATUS } from '@evinced-private/ui-common';

import { FilterParams } from 'src/types/FilterParams';

import { apiCall } from '../api/ServerApi';
import { API_URL_PATHS } from '../consts/ApiUrlPaths';
import { PaginationRequestHelper } from '../helpers/PaginationRequestHelper';
import { PaginationParams } from '../types/PaginationParams';

import { logger } from './Logger';

interface IExportToApiParams {
	exportToApiCustomerUrl: string;
	scanId: string;
	paginationParams: PaginationParams;
	filterOptions: FilterParams;
	currentItemsCount: number;
}

const MAX_SUPPORTED_PAGE_SIZE = 10000; // server supports file size of max 10000 items

export async function exportToApi({
	exportToApiCustomerUrl,
	scanId,
	filterOptions,
	paginationParams,
	currentItemsCount
}: IExportToApiParams): Promise<{ status: TOAST_STATUS; message: string }> {
	try {
		const { SCAN_RESULTS_PATH } = API_URL_PATHS;

		// if user tries to export more than 10000 items, we will fail the request with custom error message
		if (currentItemsCount > MAX_SUPPORTED_PAGE_SIZE) {
			return {
				status: TOAST_STATUS.FAIL,
				message: `Unable to export. Filter the issue count below ${MAX_SUPPORTED_PAGE_SIZE.toLocaleString()}.`
			};
		}

		// if number of items is less than the max supported page size,
		// we will need to send pagination params to the server with the current number of filtered items.
		// we do it to avoid default server pagination params (page: 1, sizePerPage: 10)
		// to get the customer error message - so we don't need to send pagination params to the server at all
		const customPaginationParams = {
			...paginationParams,
			page: 1,
			sizePerPage: currentItemsCount
		};
		const requestParams = PaginationRequestHelper.createUrlParams(
			customPaginationParams,
			filterOptions
		);

		const url = `${SCAN_RESULTS_PATH}/${scanId}/issues?export=true${requestParams}`;
		const data = await apiCall('GET', url);

		// take response JSON and pass it to customer API endpoint
		await apiCall('POST', exportToApiCustomerUrl, { data, isAbsoluteUrl: true });

		return {
			status: TOAST_STATUS.SUCCESS,
			message: 'Export complete. The data was delivered to the integration endpoint.'
		};
	} catch (error) {
		logger.error(error);
		const { response } = error;
		// in case of server error - we need to display customer error message if available
		if (response?.status === 400) {
			return {
				status: TOAST_STATUS.FAIL,
				message: response?.data?.message || 'Export failed. Please try again.'
			};
		}
		return {
			status: TOAST_STATUS.FAIL,
			message: 'Export failed. Please try again.'
		};
	}
}
